// src/pages/guides/AttackSurfaceMonitoringGuide.js
// Attack Surface Monitoring Guide - SEO Pillar Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function AttackSurfaceMonitoringGuide() {
  const startTrial = useTrialCta('guide_trial:attack-surface-monitoring');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Attack Surface Monitoring Guide",
    "description": "A tactical guide for External Attack Surface Management (EASM) and Continuous Threat Exposure Management (CTEM). Learn to scope, discover, analyze, and prioritize exposures.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/attack-surface-monitoring",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/attack-surface-monitoring"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Attack Surface Monitoring" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Attack Surface Monitoring Guide | DriftAlarm"
        description="A tactical guide for External Attack Surface Management (EASM) and Continuous Threat Exposure Management (CTEM). Learn to scope, discover, analyze, and prioritize exposures—plus a P0–P4 rubric."
        path="/guides/attack-surface-monitoring"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/attack-surface-monitoring')].filter(Boolean)}
      />
      {/* Navigation Bar */}
      <PublicHeader activeLink="guides" />

      {/* Main Content */}
      <main className="guide-content">
        {/* Hero Section */}
        <section className="guide-hero">
          <h1>Attack Surface Monitoring: How to Monitor What Attackers See</h1>
          <GuideByline path="/guides/attack-surface-monitoring" />
          <GuideAnswer path="/guides/attack-surface-monitoring" />
          <p className="guide-hero-intro">
            Your external attack surface is everything an attacker can see from the internet—domains, IPs,
            cloud services, and the vulnerabilities exposed on them. Attack surface monitoring means watching
            that surface continuously, not just scanning once and hoping nothing changes.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Scope: Define what you're protecting</li>
              <li>Discovery: Find assets you didn't know about</li>
              <li>Analysis: Map exposures and vulnerabilities</li>
              <li>Enrichment: Add context that drives decisions</li>
              <li>Prioritization: A P0–P4 rubric you can use today</li>
              <li>Cadence: How often to check, what to alert on</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/how-it-works" className="da-btn da-btn--secondary">See our methodology</Link>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#what-it-is">What It Is</a></li>
            <li><a href="#the-loop">The Monitoring Loop</a></li>
            <li><a href="#scope">Step 1: Scope</a></li>
            <li><a href="#discovery">Step 2: Discovery</a></li>
            <li><a href="#analysis">Step 3: Analysis</a></li>
            <li><a href="#enrichment">Step 4: Enrichment</a></li>
            <li><a href="#prioritization">Step 5: Prioritization (P0–P4)</a></li>
            <li><a href="#operationalize">Operationalize</a></li>
            <li><a href="#7-day-quickstart">7-Day Quickstart</a></li>
            <li><a href="#next-playbooks">Next Playbooks</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section: What It Is */}
        <section id="what-it-is" className="guide-section">
          <h2>What Attack Surface Monitoring Actually Means</h2>
          <p>
            Attack surface monitoring is the continuous process of discovering, cataloging, and assessing
            every internet-facing asset your organization exposes—then tracking changes to that surface over time.
          </p>
          <p>
            It's fundamentally different from a one-time penetration test or vulnerability scan. Those are
            snapshots. Attack surface monitoring is a video stream.
          </p>
          <div className="callout callout-info">
            <div className="callout-title">Why This Matters</div>
            <div className="callout-content">
              <p>
                <strong>External reality changes daily.</strong> New subdomains get spun up. Cloud resources
                get exposed. Developers push staging servers to production IPs. Shadow IT happens.
              </p>
              <p>
                <strong>Drift is where incidents start.</strong> Most breaches don't come from your hardened
                production systems—they come from the forgotten test server, the expired certificate, the
                misconfigured S3 bucket that nobody knew existed.
              </p>
            </div>
          </div>
          <p>
            The goal of attack surface monitoring is simple: <strong>know what attackers can see, before
            they see it first.</strong>
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Industry Terminology</div>
            <div className="callout-content">
              <p>
                Attack surface monitoring is also known as <strong>External Attack Surface Management (EASM)</strong>,
                <strong> Attack Surface Management (ASM)</strong>, or <strong>Exposure Management</strong>.
                Gartner's <strong>Continuous Threat Exposure Management (CTEM)</strong> framework encompasses
                attack surface monitoring as a core component alongside vulnerability management and threat validation.
              </p>
            </div>
          </div>
        </section>

        {/* Section: The Monitoring Loop */}
        <section id="the-loop" className="guide-section">
          <h2>The Attack Surface Monitoring Loop</h2>
          <p>
            Effective attack surface monitoring follows a continuous cycle. Each step feeds into the next,
            and the loop never stops.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Scope & Asset Register</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Discover & Validate</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Exposure Mapping</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Vulnerability Probing</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">5</div>
              <div className="step-card-title">Enrichment & Context</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">6</div>
              <div className="step-card-title">Prioritization & Workflow</div>
            </div>
          </div>

          <p>
            The rest of this guide walks through each step in detail, with practical guidance you can
            implement regardless of what tools you use.
          </p>
        </section>

        {/* Section: Scope */}
        <section id="scope" className="guide-section">
          <h2>Step 1 — Define Your Scope</h2>
          <p>
            Before you can monitor your attack surface, you need to know what belongs to you. This sounds
            obvious, but most organizations dramatically underestimate the assets they expose to the internet.
          </p>

          <h3>Asset Categories to Track</h3>
          <ul>
            <li><strong>Domains & DNS</strong> — Primary domains, subdomains, and any DNS records you control</li>
            <li><strong>IP Ranges</strong> — Owned IP space, cloud-allocated IPs, and ranges behind CDNs or WAFs</li>
            <li><strong>Cloud Assets</strong> — AWS accounts, Azure subscriptions, GCP projects, and their external endpoints</li>
            <li><strong>Third-Party SaaS</strong> — Entry points like SSO portals, customer-facing apps, and API endpoints</li>
            <li><strong>Brand & Identity Surface</strong> — Keywords, typosquatting domains, look-alike sites</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Owned vs Managed vs Influenced</div>
            <div className="callout-content">
              <p><strong>Owned:</strong> Assets you directly control (your servers, your domains)</p>
              <p><strong>Managed:</strong> Assets a third party operates on your behalf (managed hosting, SaaS with your branding)</p>
              <p><strong>Influenced:</strong> Assets that affect you but you don't control (partner integrations, vendor APIs)</p>
              <p>You need visibility into all three, but your remediation authority differs for each.</p>
            </div>
          </div>

          <h3>Asset Register Template</h3>
          <p>
            Start with a simple register. You can use a spreadsheet, a CMDB, or a purpose-built tool—the
            format matters less than having one.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Asset Type</th>
                  <th>Identifier</th>
                  <th>Owner</th>
                  <th>Environment</th>
                  <th>Criticality</th>
                  <th>Monitoring Notes</th>
                  <th>Change Control</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Domain</td>
                  <td>example.com</td>
                  <td>IT Ops</td>
                  <td>Production</td>
                  <td>High</td>
                  <td>Primary customer-facing</td>
                  <td>CAB required</td>
                </tr>
                <tr>
                  <td>IP Range</td>
                  <td>203.0.113.0/24</td>
                  <td>Infrastructure</td>
                  <td>Production</td>
                  <td>High</td>
                  <td>Datacenter allocation</td>
                  <td>Standard</td>
                </tr>
                <tr>
                  <td>Cloud</td>
                  <td>AWS: 123456789012</td>
                  <td>DevOps</td>
                  <td>Mixed</td>
                  <td>Medium</td>
                  <td>Multiple VPCs, public ELBs</td>
                  <td>Terraform managed</td>
                </tr>
                <tr>
                  <td>SaaS</td>
                  <td>login.vendor.com/acme</td>
                  <td>HR</td>
                  <td>Production</td>
                  <td>Medium</td>
                  <td>Employee SSO portal</td>
                  <td>Vendor managed</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Discovery */}
        <section id="discovery" className="guide-section">
          <h2>Step 2 — Discovery & Validation</h2>
          <p>
            Discovery finds what you missed—the orphaned subdomains, the forgotten staging environments,
            the shadow SaaS someone in marketing spun up. It's where attack surface monitoring delivers
            the most immediate value.
          </p>

          <h3>Passive Discovery</h3>
          <p>Passive techniques query existing data sources without touching your infrastructure:</p>
          <ul>
            <li>Certificate Transparency logs (crt.sh, Censys)</li>
            <li>DNS zone enumeration and history</li>
            <li>Public cloud asset discovery (exposed S3 buckets, Azure blobs)</li>
            <li>Search engine indexing (Google dorking, Shodan)</li>
            <li>WHOIS and registrar data</li>
          </ul>

          <h3>Active Discovery</h3>
          <p>Active techniques directly probe your assets:</p>
          <ul>
            <li>Subdomain brute-forcing against known wordlists</li>
            <li>Port scanning across IP ranges</li>
            <li>Virtual host enumeration</li>
            <li>Web crawling and spidering</li>
          </ul>

          <h3>Validation & Deduplication</h3>
          <p>
            Raw discovery generates noise. Before adding assets to your inventory, validate:
          </p>
          <ul>
            <li><strong>Resolves:</strong> Does the DNS record actually resolve?</li>
            <li><strong>Reachable:</strong> Can you connect to it from the internet?</li>
            <li><strong>In-scope:</strong> Does it belong to you, or is it a false positive?</li>
            <li><strong>Deduplicated:</strong> Is it a duplicate of an existing asset?</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Noise Kills Programs</div>
            <div className="callout-content">
              <p>
                A discovery tool that dumps 10,000 "findings" with 30% false positives will burn out your
                team in weeks. Invest in validation. The goal is a trustworthy inventory, not a long list.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Analysis */}
        <section id="analysis" className="guide-section">
          <h2>Step 3 — Digital Analysis Pipeline</h2>
          <p>
            Once you have a validated inventory, the next step is understanding what's running on those
            assets and whether it's vulnerable.
          </p>

          <h3>Exposure Mapping</h3>
          <p>
            For each asset, determine what's exposed:
          </p>
          <ul>
            <li><strong>Port scanning:</strong> What TCP/UDP ports are open?</li>
            <li><strong>Service identification:</strong> What's running on each port?</li>
            <li><strong>Version fingerprinting:</strong> What version of each service?</li>
            <li><strong>Protocol enumeration:</strong> HTTP headers, SSL/TLS configuration, banner data</li>
          </ul>
          <p>
            The output is a catalog: "Asset X has SSH on 22, HTTPS on 443 running nginx/1.18.0,
            and an FTP server on 21."
          </p>

          <h3>Vulnerability Probing</h3>
          <p>
            With services mapped, probe for vulnerabilities:
          </p>
          <ul>
            <li><strong>Service-specific checks:</strong> CVEs affecting the observed versions</li>
            <li><strong>Web application checks:</strong> OWASP Top 10, CMS vulnerabilities, exposed admin panels</li>
            <li><strong>Configuration issues:</strong> Default credentials, missing security headers, open directories</li>
            <li><strong>SSL/TLS weaknesses:</strong> Expired certs, weak ciphers, protocol downgrade risks</li>
          </ul>

          <h3>Evidence & Confidence</h3>
          <p>
            Not all findings are equal. Every vulnerability should have:
          </p>
          <ul>
            <li><strong>Evidence:</strong> What did you observe? (response, screenshot, output)</li>
            <li><strong>Confidence level:</strong> High (verified), Medium (likely), Low (possible)</li>
            <li><strong>Validation status:</strong> Confirmed, Unconfirmed, False Positive</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Low Confidence = Not P0</div>
            <div className="callout-content">
              <p>
                A finding with low confidence should never escalate to your highest priority. Verify first.
                Low confidence findings cap at P2 until validated.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Enrichment */}
        <section id="enrichment" className="guide-section">
          <h2>Step 4 — Enrichment</h2>
          <p>
            Raw vulnerability data doesn't tell you what to fix first. Enrichment adds the context that
            turns findings into actionable decisions.
          </p>

          <h3>Context Factors</h3>
          <ul>
            <li>
              <strong>Internet Exposure:</strong> Is this reachable from anywhere on the internet, or
              restricted by VPN, allowlist, or network segmentation?
            </li>
            <li>
              <strong>Asset Criticality:</strong> Is this an authentication system, payment processor,
              admin panel, or customer data store? Or a static marketing site?
            </li>
            <li>
              <strong>Exploitability Signals:</strong> Is there a public exploit? Is it trivial to weaponize,
              or does it require specific conditions?
            </li>
            <li>
              <strong>EOL/Unsupported Status:</strong> Is the software end-of-life? No more patches means
              permanent risk.
            </li>
            <li>
              <strong>Compensating Controls:</strong> Is there MFA protecting access? A WAF blocking exploit
              patterns? An allowlist limiting who can connect?
            </li>
            <li>
              <strong>Ownership & Routing:</strong> Who owns this asset? Who can actually fix it?
            </li>
          </ul>
          <p>
            The goal is to answer: "Given everything we know about this finding and this asset, how urgent
            is this really?"
          </p>
        </section>

        {/* Section: Prioritization */}
        <section id="prioritization" className="guide-section">
          <h2>Step 5 — Prioritization Rubric (P0–P4)</h2>
          <p>
            This is the heart of attack surface management: turning enriched findings into a prioritized
            queue your team can actually work through.
          </p>

          <h3>Priority Definitions</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Definition</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="priority-badge p0">P0</span></td>
                  <td>Incident / confirmed exposure or compromise</td>
                  <td>Immediate (incident response)</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p1">P1</span></td>
                  <td>High-likelihood, high-impact exposure</td>
                  <td>Hours to 72 hours</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p2">P2</span></td>
                  <td>Significant exposure, needs prompt action</td>
                  <td>This sprint / 1-2 weeks</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p3">P3</span></td>
                  <td>Moderate risk, scheduled remediation</td>
                  <td>Next 30-60 days</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p4">P4</span></td>
                  <td>Low risk, track and monitor</td>
                  <td>Backlog / informational</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-critical">
            <div className="callout-title">P0 Requires Validation</div>
            <div className="callout-content">
              <p>
                P0 is reserved for <strong>confirmed</strong> incidents or exposures—not suspected ones.
                Before escalating to P0, you must validate the finding. A false positive P0 wastes incident
                response resources and erodes trust in your program.
              </p>
            </div>
          </div>

          <h3>Instant P1 Triggers</h3>
          <p>
            Certain findings should automatically escalate to P1 when exposed to the internet
            <strong> without strong compensating controls</strong> (like IP allowlist, VPN requirement,
            or MFA gateway):
          </p>
          <ul>
            <li><strong>Management/remote access ports:</strong> SSH (22), RDP (3389), LDAP (389/636),
                WinRM (5985/5986), VNC (5900+), Telnet (23)</li>
            <li><strong>EOL software:</strong> Any end-of-life software exposed externally without mitigation</li>
            <li><strong>Potential cloud storage exposure:</strong> S3 buckets, Azure blobs, or GCS buckets
                with read/write access (P1 until validated; becomes P0 if sensitive data confirmed)</li>
            <li><strong>Database ports:</strong> MySQL (3306), PostgreSQL (5432), MongoDB (27017),
                Redis (6379), Elasticsearch (9200) exposed directly</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">External Exposure Changes Everything</div>
            <div className="callout-content">
              <p>
                The same service can be a medium risk internally but a P1 on the internet. An RDP server
                behind a VPN is a different risk than RDP exposed to 0.0.0.0/0. Always factor in
                exposure when prioritizing.
              </p>
            </div>
          </div>

          <h3>P0 Criteria (Validation Required)</h3>
          <p>A finding becomes P0 only when you have <strong>confirmed</strong>:</p>
          <ul>
            <li>Sensitive data is exposed (you've verified the contents)</li>
            <li>Valid credentials or secrets are exposed (tested or verified)</li>
            <li>Active exploitation or compromise indicators are present</li>
            <li>Takeover risk is proven (e.g., dangling DNS with verified subdomain takeover)</li>
          </ul>

          <h3>Scoring Model</h3>
          <p>
            For findings that aren't instant P1 triggers, use a simple scoring model to separate
            P1/P2/P3/P4:
          </p>

          <div className="scoring-formula">
            <div className="scoring-formula-title">Score Calculation</div>
            <div className="scoring-formula-line">
              <code>Base Severity</code> (1–10 based on vulnerability type)
            </div>
            <div className="scoring-formula-line">
              + <code>Exposure Modifier</code>: Internet-facing +6, Authenticated +4, Internal +1
            </div>
            <div className="scoring-formula-line">
              + <code>Exploitability</code>: Public exploit +5, Likely exploitable +3, Hard to exploit +1
            </div>
            <div className="scoring-formula-line">
              + <code>Business Criticality</code>: High +5, Medium +3, Low +1
            </div>

            <div className="scoring-thresholds">
              <div className="scoring-threshold">
                <span className="scoring-threshold-score">Score ≥ 18</span>
                <span className="priority-badge p1">P1</span>
              </div>
              <div className="scoring-threshold">
                <span className="scoring-threshold-score">Score 13–17</span>
                <span className="priority-badge p2">P2</span>
              </div>
              <div className="scoring-threshold">
                <span className="scoring-threshold-score">Score 8–12</span>
                <span className="priority-badge p3">P3</span>
              </div>
              <div className="scoring-threshold">
                <span className="scoring-threshold-score">Score &lt; 8</span>
                <span className="priority-badge p4">P4</span>
              </div>
            </div>
          </div>

          <p><strong>Override rules:</strong></p>
          <ul>
            <li><strong>Low confidence caps at P2:</strong> Until verified, don't escalate beyond P2</li>
            <li><strong>EOL + internet-facing = P1:</strong> No exceptions unless strong mitigation exists</li>
            <li><strong>P0 is not score-based:</strong> P0 is validation-based. No score automatically
                generates P0.</li>
          </ul>

          <h3>Examples</h3>
          <div className="example-box">
            <div className="example-title">Example 1: Exposed RDP</div>
            <div className="example-content">
              <p>
                <strong>Finding:</strong> RDP (3389) detected on server in your IP range, reachable from internet.
              </p>
              <p>
                <strong>Assessment:</strong> RDP is a management port. Internet-facing management access without
                VPN or allowlist is an instant P1 trigger. No scoring needed—this goes straight to P1.
              </p>
              <p>
                <strong>Action:</strong> Validate reachability, identify owner, restrict access within 72 hours.
              </p>
            </div>
          </div>

          <div className="example-box">
            <div className="example-title">Example 2: Public Storage Bucket</div>
            <div className="example-content">
              <p>
                <strong>Finding:</strong> Monitoring detected an S3 bucket with public read/write access.
              </p>
              <p>
                <strong>Assessment:</strong> Public cloud storage is a P1 trigger <em>until validated</em>.
                Initial priority: P1.
              </p>
              <p>
                <strong>Validation:</strong> Upon review, the bucket contains customer PII.
              </p>
              <p>
                <strong>Result:</strong> Confirmed sensitive data exposure → escalate to P0, engage incident response.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Operationalize */}
        <section id="operationalize" className="guide-section">
          <h2>Operationalize Your Monitoring</h2>
          <p>
            Attack surface monitoring only works if it's operationalized—built into your team's daily
            and weekly rhythm.
          </p>

          <h3>Cadence</h3>
          <ul>
            <li><strong>Daily:</strong> Change detection on critical assets (new hosts, new ports,
                configuration drift)</li>
            <li><strong>Weekly:</strong> Deeper vulnerability checks, new CVE correlation, discovery refresh</li>
            <li><strong>Monthly:</strong> Full baseline refresh, scope review, asset register audit</li>
          </ul>

          <h3>Alert Thresholds</h3>
          <p>
            Don't alert on everything—you'll burn out. Alert on signals that matter:
          </p>
          <ul>
            <li>New management port exposed (SSH, RDP, LDAP)</li>
            <li>New unknown host in your IP space</li>
            <li>High-severity external vulnerability detected</li>
            <li>New public cloud storage exposure</li>
            <li>SSL certificate expiring within 14 days</li>
            <li>EOL software detected on internet-facing asset</li>
          </ul>

          <h3>Workflow</h3>
          <ol>
            <li><strong>Assign owner:</strong> Every finding needs an owner, not a team alias</li>
            <li><strong>Create ticket:</strong> Include evidence, priority, and remediation guidance</li>
            <li><strong>Verify fix:</strong> Re-scan after remediation to confirm resolution</li>
            <li><strong>Prevention control:</strong> Ask "how do we prevent this class of issue?"</li>
          </ol>

          <h3>Metrics to Track</h3>
          <ul>
            <li><strong>Unknown assets discovered:</strong> How many assets did discovery find that
                weren't in your register?</li>
            <li><strong>Mean time to mitigate P1s:</strong> How fast are you closing critical findings?</li>
            <li><strong>Repeat exposures:</strong> Are the same issues recurring? That's a process problem.</li>
            <li><strong>Coverage:</strong> What percentage of your known surface is actively monitored?</li>
          </ul>
        </section>

        {/* Section: 7-Day Quickstart */}
        <section id="7-day-quickstart" className="guide-section">
          <h2>7-Day Quickstart Checklist</h2>
          <p>
            You can stand up a basic attack surface monitoring program in a week. Here's a day-by-day
            checklist for SMB IT leaders:
          </p>

          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 1</span>
                <span className="quickstart-day-title">Inventory What You Know</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>List all domains you own (check registrar accounts)</li>
                <li>Document your IP ranges (check hosting providers, cloud accounts)</li>
                <li>Identify your cloud accounts (AWS, Azure, GCP)</li>
                <li>Create your initial asset register spreadsheet</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 2</span>
                <span className="quickstart-day-title">Run Passive Discovery</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Search Certificate Transparency logs for your domains</li>
                <li>Check DNS records for all known domains</li>
                <li>Search Shodan/Censys for your IP ranges</li>
                <li>Add any new assets found to your register</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 3</span>
                <span className="quickstart-day-title">Validate & Classify</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Verify each discovered asset is actually yours</li>
                <li>Remove false positives from your inventory</li>
                <li>Assign criticality ratings (High/Medium/Low)</li>
                <li>Identify asset owners</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 4</span>
                <span className="quickstart-day-title">Initial Port & Service Scan</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Scan your IP ranges for open ports</li>
                <li>Identify services running on each port</li>
                <li>Flag any management ports (SSH, RDP, LDAP)</li>
                <li>Document findings in your register</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 5</span>
                <span className="quickstart-day-title">Vulnerability Assessment</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Run vulnerability scans against discovered services</li>
                <li>Check SSL/TLS configurations</li>
                <li>Look for EOL/unsupported software</li>
                <li>Prioritize findings using the P0–P4 rubric</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 6</span>
                <span className="quickstart-day-title">Triage P1s and P2s</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Create tickets for all P1 findings</li>
                <li>Assign owners and due dates</li>
                <li>Begin remediation on critical issues</li>
                <li>Document compensating controls if remediation is delayed</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Day 7</span>
                <span className="quickstart-day-title">Set Up Continuous Monitoring</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Schedule automated daily/weekly scans</li>
                <li>Configure alerts for P1 triggers</li>
                <li>Set calendar reminder for monthly baseline refresh</li>
                <li>Brief your team on the new process</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Related Playbooks */}
        <section id="next-playbooks" className="guide-section">
          <h2>Related Security Playbooks</h2>
          <p>
            This guide covered the fundamentals. Use these situation-specific playbooks for
            common findings:
          </p>

          <div className="playbook-links">
            <Link to="/guides/exposed-rdp" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Exposed RDP (3389)</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/exposed-ssh" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Exposed SSH (22)</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/exposed-admin-panels" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Exposed Admin Panels</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/cloud-storage-exposure" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Cloud Storage Exposure (S3/Blob/GCS)</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/eol-software" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">EOL Software Remediation</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/exposed-database-ports" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Exposed Database Ports</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/configuration-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Configuration Drift</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <Link to="/guides/attack-surface-checklist" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Checklist for SMBs</span>
              <span className="playbook-link-arrow">→</span>
            </Link>
            <div className="playbook-link">
              <span className="playbook-link-title">Exposed LDAP (389/636)</span>
              <span className="coming-soon">Coming Soon</span>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/attack-surface-monitoring" />
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default AttackSurfaceMonitoringGuide;
